import { Box, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useSelectedPatient from "../../../../../../../hooks/local-storage/use-selected-patient";
import CustomTextArea from "../../../../../../../components/CustomTextArea";
import Flex from "../../../../../../../components/Flex";
import LoadingOverlay from "../../../../../../../components/LoadingOverlay";
import { NoDataAvailable } from "../../../../../../../components/NoDataAvailable";
import Text from "../../../../../../../components/Text";
import ConfirmationModal from "../../../../../../../components/app/ConfirmationModal";
import DeleteModal from "../../../../../../../components/app/DeleteModal";
import {
  CHART_COLORS,
  CHART_TYPOGRAPHY,
} from "../../../../../../../components/app/provider/patient/chart";
import { PROVIDER_PATHS } from "../../../../../../../constants/paths";
import useCurrentUser from "../../../../../../../hooks/local-storage/use-current-user";
import useDisclosure from "../../../../../../../hooks/use-disclosure";
import { useReduceDisclosure } from "../../../../../../../hooks/use-reduce-disclosure";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import PatientAssessmentFormModal from "../../../../../../../layout/app/patient-portal/dashboard/actions/PatientAssessmentFormModal";
import PatientIntakeFormModal from "../../../../../../../layout/app/patient-portal/dashboard/actions/PatientIntakeFormModal";
import ResolvePatientTask from "../../../../../../../layout/app/patient-portal/patient-task/ResolvePatientTask";
import { PATIENT_INQUIRY_STATUS_COUNTS_KEY } from "../../../../../../../queries/collaboration/patient-inquiry/use-get-patient-inquiry-status-counts";
import { useUpdatePatientInquiryStatus } from "../../../../../../../queries/collaboration/patient-inquiry/use-update-patient-inquiry-status";
import { useEncounterSummeryV2 } from "../../../../../../../queries/encounter/use-encounter-summery-v2";
import { usePatientIntakeFormList } from "../../../../../../../queries/patient-portal/forms/use-patient-intake-form-list";
import { PatientInquiryStatusUpdateStatusEnum } from "../../../../../../../services";
import {
  formatDate,
  formatTime,
} from "../../../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../../../../utils/utils";
import { ACTIVITY_MAPPING } from "../../../../collabration/collaboration-task-constant";
import BatchResolveDrawer from "../../../../collabration/tasks/BatchResolveDrawer";
import SendBulkEmailModal from "../../../../communications/email/SendBulkEmailModal";
import QuestionnaireDetailsDrawer from "../../../../dashboard/sections/dashboard-questionnaire-review/AssementPreviewDrawer";
import SMSModal from "../../../../dashboard/sections/dashboard-questionnaire-review/SMSModal";
import { ContactDialModal } from "../../../../dashboard/twilio/ContactDial";
import AssessmentDrawer from "../../../../scheduling/appointment/encounter/Assessment/AssessmentDrawer";
import IntakePreviewModal, {
  IntakeFormSection,
} from "../../../../scheduling/appointment/encounter/content-section/intake-preview/IntakePreviewModal";
import SignAndLockDrawer from "../../../../scheduling/appointment/encounter/default-forms/SignAndLockDrawer";
import DateStrip from "./DateStrip";
import LinkButton from "./LinkButton";

interface PatientRequiringActionProps {
  data: any;
  patientPortalSide?: boolean;
  isLoadingData?: boolean;
  /** True once every available record has been loaded — shows the end-of-list note. */
  isEndOfList?: boolean;
}

type RequiringActionOperation =
  | "TASK"
  | "ENCOUNTER_COMPLETE"
  | "ENCOUNTER_SIGNOFF"
  | "INTAKE_REVIEW"
  | "ASSESSMENT_REVIEW"
  | "TASK_ACTION"
  | "PATIENT_MESSAGE"
  | "ASSESSMENT"
  | "INTAKE"
  | "APPOINTMENT_REQUEST"
  | "APPOINTMENT_RESCHEDULE_REQUEST"
  | "APPOINTMENT_CANCEL_REQUEST";

const ACTION_NAME_MAPPER: Record<RequiringActionOperation, string> = {
  TASK: "Resolve Task",
  ENCOUNTER_COMPLETE: "Complete & Sign Note",
  ENCOUNTER_SIGNOFF: "Sign Visit Note",
  INTAKE_REVIEW: "Intake Review",
  ASSESSMENT_REVIEW: "Assessment Review",
  ASSESSMENT: "Submit Assessment",
  TASK_ACTION: "",
  PATIENT_MESSAGE: "Patient Message",
  INTAKE: "Submit Intake",
  APPOINTMENT_REQUEST: "",
  APPOINTMENT_RESCHEDULE_REQUEST: "",
  APPOINTMENT_CANCEL_REQUEST: "",
};

const APPOINTMENT_REQUEST_OPERATIONS: RequiringActionOperation[] = [
  "APPOINTMENT_REQUEST",
  "APPOINTMENT_RESCHEDULE_REQUEST",
  "APPOINTMENT_CANCEL_REQUEST",
];

const isAppointmentRequestOperation = (
  op?: string
): op is
  | "APPOINTMENT_REQUEST"
  | "APPOINTMENT_RESCHEDULE_REQUEST"
  | "APPOINTMENT_CANCEL_REQUEST" =>
  !!op &&
  APPOINTMENT_REQUEST_OPERATIONS.includes(op as RequiringActionOperation);

const PatientRequiringAction = ({
  data,
  patientPortalSide = false,
  isLoadingData,
  isEndOfList,
}: PatientRequiringActionProps) => {
  const theme = useTheme();
  const dates = Object.keys(data ?? {});
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();

  const [me] = useCurrentUser();
  const { mutateAsync: updateInquiryStatus } = useUpdatePatientInquiryStatus();
  const denyRequestDisclosure = useDisclosure(false);
  const [denyReason, setDenyReason] = useState<string>("");
  const [appointmentRequestEvent, setAppointmentRequestEvent] =
    useState<any>(null);

  const [selectedPatient] = useSelectedPatient();
  const { patientId } = useParams() as { patientId: any };

  const getActionName = (
    action: RequiringActionOperation,
    taskAction: string
  ) => {
    switch (action) {
      case "TASK_ACTION": {
        const activityConfig =
          ACTIVITY_MAPPING[taskAction as keyof typeof ACTIVITY_MAPPING];

        if (!activityConfig?.icon) return null;

        const Icon = activityConfig.icon;
        return <Icon fontSize="small" sx={{ color: activityConfig.color }} />;
      }

      default:
        return ACTION_NAME_MAPPER[action] ?? "";
    }
  };
  const [selectedRequestedAction, setSelectedRequestedAction] = useState<any>();
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string>();
  const [shouldOpenIntakePreview, setShouldOpenIntakePreview] =
    useState<boolean>(false);
  // Tracks the event pending a sign-off drawer open while the encounter
  // summary data is being fetched via v2 (get-by-ID) API.
  const [pendingSignEvent, setPendingSignEvent] = useState<any>(null);

  const {
    data: signEncounterSummaryData,
    isLoading: isSignEncounterLoading,
    isFetching: isSignEncounterFetching,
  } = useEncounterSummeryV2({
    appointmentUuid: pendingSignEvent?.appointmentId,
    xTENANTID: getXtenantId(),
  });
  // const [shouldOpenScreenerPreview, setShouldOpenScreenerPreview] =
  //   useState<boolean>(false);
  // const [selectedForm, setSelectedForm] = useState<any>();

  const {
    data: intakeFormsData,
    isLoading,
    isRefetching,
  } = usePatientIntakeFormList({
    appointmentUuid: selectedAppointmentId,
    pagination: DEF_PAGINATION,
    xTENANTID: getXtenantId(),
  });

  useEffect(() => {
    if (
      shouldOpenIntakePreview &&
      intakeFormsData?.data?.content?.[0] &&
      !(isLoading || isRefetching)
    ) {
      previewIntakeDisc.open(intakeFormsData.data.content[0]);
      setShouldOpenIntakePreview(false);
    }
  }, [intakeFormsData, isLoading, shouldOpenIntakePreview, isRefetching]);

  // When sign-off is requested, we first fetch the full encounter summary via
  // get-encounter-by-ID (v2) and only then open the SignAndLockDrawer with the
  // merged payload. This avoids the empty-fields bug (ECRH-4164) where the
  // drawer was previously opened with only `{ uuid }` and the subsequent PDF
  // preview showed no ICD/CPT/clinical details.
  useEffect(() => {
    if (
      pendingSignEvent &&
      signEncounterSummaryData?.data &&
      !(isSignEncounterLoading || isSignEncounterFetching)
    ) {
      signDrawerDisclosure.open({
        ...pendingSignEvent,
        prefillData: signEncounterSummaryData.data,
      });
      setPendingSignEvent(null);
    }
  }, [
    pendingSignEvent,
    signEncounterSummaryData,
    isSignEncounterLoading,
    isSignEncounterFetching,
  ]);

  const handleAction = (event: any) => {
    const action =
      event?.requiringActionOperation || event?.patientRequiringActionOperation;
    setSelectedRequestedAction(event);

    switch (action) {
      case "ENCOUNTER_COMPLETE": {
        navigate(
          `${PROVIDER_PATHS.NEW_PATIENT}/${event?.patient}/visit-note/form?appointmentid=${event?.appointmentId}&noteType=SIMPLE_SOAP_NOTE`
        );
        return;
      }
      case "ENCOUNTER_SIGNOFF": {
        // Fetch the full encounter summary via get-encounter-by-ID (v2)
        // before opening the SignAndLockDrawer so the PDF preview displays
        // saved ICD/CPT codes and clinical details (ECRH-4164).
        setPendingSignEvent(event);
        return;
      }
      case "INTAKE_REVIEW": {
        setSelectedAppointmentId(event.appointmentId);
        setShouldOpenIntakePreview(true);
        return;
      }
      case "ASSESSMENT_REVIEW": {
        if (event?.screenerType) questionnaireDetailsDisclosure.open(event);
        return;
      }
      case "TASK_ACTION": {
        if (event?.taskAction === "Call") {
          contactDialModalDisclosure.open(patientId);
          return;
        }
        if (event?.taskAction === "Message") {
          smsModalDisclosure.open({
            patientUuid: event?.patient,
            patientName: event?.patientName,
          });
          return;
        }
        if (event?.taskAction === "Email") {
          sendMailDisclosure.open([
            { uuid: event?.patient, name: event?.patientName },
          ]);
        }
        return;
      }
      case "PATIENT_MESSAGE": {
        smsModalDisclosure.open({
          patientUuid: event?.patient,
          patientName: event?.patientName,
        });
        return;
      }
      case "TASK": {
        handleConfirmResolve();
        return;
      }
      case "ASSESSMENT": {
        const formType = event?.forms;
        assessmentFormModalDisc.open({
          appointmentId: event?.appointmentId,
          patientId: event?.patient,
          assessmentType: formType,
          actionId: event?.uuid,
        });
        return;
      }
      case "INTAKE": {
        intakeFormModalDisc.open({
          appointmentId: event?.appointmentId,
          patientId: me?.uuid,
          intakeForms: event?.forms,
          actionId: event?.uuid,
        });
        return;
      }
    }
  };

  const refetchRequiringAction = () => {
    queryClient.invalidateQueries({
      queryKey: ["requiring-action"],
    });
    queryClient.invalidateQueries({
      queryKey: ["patient-requiring-action"],
    });
    queryClient.invalidateQueries({
      queryKey: [PATIENT_INQUIRY_STATUS_COUNTS_KEY],
    });
  };

  const handleApproveAppointmentRequest = async (event: any) => {
    const inquiryId = event?.inquiryId;
    if (!inquiryId) {
      showSnackbar({
        type: "error",
        message: "Inquiry id is missing for this request.",
      });
      return;
    }

    try {
      const res = await updateInquiryStatus({
        inquiryId,
        patientInquiryStatusUpdate: {
          status: PatientInquiryStatusUpdateStatusEnum.Confirmed,
        },
        xTENANTID: getXtenantId(),
      });
      showSnackbar(getSuccessSnackbar(res));
      refetchRequiringAction();
    } catch (error) {
      showSnackbar(getErrorSnackbar(error));
    }
  };

  const openDenyAppointmentRequest = (event: any) => {
    setAppointmentRequestEvent(event);
    denyRequestDisclosure.open();
  };

  const closeDenyAppointmentRequest = () => {
    denyRequestDisclosure.close();
    setAppointmentRequestEvent(null);
    setDenyReason("");
  };

  const resolveTaskDisclosureHost = useDisclosure(false, {
    onClose: refetchRequiringAction,
  });

  const handleConfirmResolve = () => {
    resolveTaskDisclosureHost.open([selectedRequestedAction?.taskId]);
    confirmResolveDisc.close();
  };

  const [
    signDrawerDisclosure,
    sendMailDisclosure,
    confirmResolveDisc,
    previewAssessmentDisc,
    questionnaireDetailsDisclosure,
    smsModalDisclosure,
    contactDialModalDisclosure,
    intakeFormModalDisc,
    assessmentFormModalDisc,
  ] = useReduceDisclosure(9);

  const previewIntakeDisc = useDisclosure(false, {
    onClose: () => {
      setSelectedAppointmentId(undefined);
      setShouldOpenIntakePreview(false);
    },
  });

  let globalRowIndex = 0;

  if (dates?.length === 0 && !isLoading && !isRefetching)
    return (
      <Box height={"100%"}>
        <NoDataAvailable message="No Requiring Action Found" />
      </Box>
    );

  return (
    <>
      <IntakePreviewModal
        disclosureHost={previewIntakeDisc}
        // `activeSections` is a comma-joined list of the same section keys.
        selectedSection={
          intakeFormsData?.data?.content[0]?.activeSections?.split(",") as
            IntakeFormSection[] | undefined
        }
        resolveRequiringActionId={selectedRequestedAction?.uuid}
      />

      {previewAssessmentDisc.opened && (
        <AssessmentDrawer
          disclosureHost={previewAssessmentDisc}
          appointmentIdParam={selectedRequestedAction?.appointmentId}
          requiringActionId={selectedRequestedAction?.uuid}
        />
      )}
      {questionnaireDetailsDisclosure.opened && (
        <QuestionnaireDetailsDrawer
          disclosureHost={questionnaireDetailsDisclosure}
          questionnaire={{
            ...questionnaireDetailsDisclosure?.data,
            patientId: questionnaireDetailsDisclosure?.data?.patient,
            patientMrn: selectedPatient?.mrn,
          }}
          allowReview
          requiringActionId={questionnaireDetailsDisclosure?.data?.uuid}
        />
      )}
      {signDrawerDisclosure.opened && (
        <SignAndLockDrawer
          disclosureHost={signDrawerDisclosure}
          prefillSummeryData={{
            uuid: signDrawerDisclosure?.data?.encounterSummaryId,
          }}
        />
      )}

      {patientPortalSide && resolveTaskDisclosureHost.opened && (
        <ResolvePatientTask disclosureHost={resolveTaskDisclosureHost} />
      )}

      {resolveTaskDisclosureHost.opened && !patientPortalSide && (
        <BatchResolveDrawer disclosureHost={resolveTaskDisclosureHost} />
      )}

      {sendMailDisclosure.opened && (
        <SendBulkEmailModal
          disClosureHost={sendMailDisclosure}
          onSave={() => confirmResolveDisc.open()}
        />
      )}
      {smsModalDisclosure.opened && (
        <SMSModal
          disclosureHost={smsModalDisclosure}
          patientUuid={smsModalDisclosure?.data?.patientUuid}
          patientName={smsModalDisclosure?.data?.patientName}
        />
      )}
      {contactDialModalDisclosure.opened && (
        <ContactDialModal
          disclosureHost={contactDialModalDisclosure}
          // contactNumber={patientData?.mobileNumber}
        />
      )}

      {intakeFormModalDisc.opened && (
        <PatientIntakeFormModal disclosureHost={intakeFormModalDisc} />
      )}

      {assessmentFormModalDisc.opened && (
        <PatientAssessmentFormModal disclosureHost={assessmentFormModalDisc} />
      )}

      <ConfirmationModal
        maxWidth={"sm"}
        onConfirm={handleConfirmResolve}
        disclosureHost={confirmResolveDisc}
        confirmationText="Yes"
        cancelText="No"
        title={
          <Text fontWeight={"medium"} fontSize={"24px"}>
            Do you want resolve this task?
          </Text>
        }
      />

      <DeleteModal
        disableOnSaveSnackbar
        w="450px"
        open={denyRequestDisclosure.opened}
        onClose={closeDenyAppointmentRequest}
        onConfirmation={async () => {
          if (!denyReason?.trim()) {
            return Promise.reject({
              response: {
                data: { message: "Reason is a required field" },
              },
            });
          }
          const inquiryId = appointmentRequestEvent?.inquiryId;
          if (!inquiryId) {
            return Promise.reject({
              response: {
                data: {
                  message: "Inquiry id is missing for this request.",
                },
              },
            });
          }

          return updateInquiryStatus({
            inquiryId,
            patientInquiryStatusUpdate: {
              status: PatientInquiryStatusUpdateStatusEnum.Rejected,
              rejectReason: denyReason.trim(),
            },
            xTENANTID: getXtenantId(),
          }).then((res) => {
            showSnackbar(getSuccessSnackbar(res));
            refetchRequiringAction();
            closeDenyAppointmentRequest();
            return res;
          });
        }}
        content={
          (
            <Box>
              <Text align="center">
                Are you sure you want to deny this appointment request?
              </Text>
              <Text align="center">
                Please specify the reason for the denial.
              </Text>
            </Box>
          ) as any
        }
        input={
          <Box width="100%">
            <CustomTextArea
              required
              label="Reason"
              value={denyReason}
              minRows={5}
              placeholder="Enter reason here..."
              onChange={(e) => setDenyReason(e.currentTarget.value)}
            />
          </Box>
        }
        confirmButtonText="Deny"
        cancelButtonText="Cancel"
      />

      <LoadingOverlay isLoading={isLoading || isRefetching} />

      <Flex flexDirection="column" sx={{ width: "100%", height: "100%" }}>
        {dates?.map((date) => (
          <React.Fragment key={date}>
            <DateStrip date={formatDate(date) ?? ""} />
            {data?.[date]?.map((event: any) => {
              const rowBg =
                globalRowIndex % 2 === 0
                  ? CHART_COLORS.surfaceWhite
                  : CHART_COLORS.surfaceAlt;
              globalRowIndex++;

              return (
                <Flex
                  key={event?.uuid || globalRowIndex}
                  alignItems="center"
                  gap={1.5}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    backgroundColor: rowBg,
                    borderLeft: `1px solid ${CHART_COLORS.border}`,
                    borderRight: `1px solid ${CHART_COLORS.border}`,
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "#F2F6F8" },
                  }}
                >
                  <Flex
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      flexShrink: 0,
                      background: CHART_COLORS.surfaceAlt,
                      borderRadius: "999px",
                      width: 34,
                      height: 34,
                      fontSize: 16,
                      color: theme.palette.primary.main,
                    }}
                  >
                    {event.actionTypeEmoji}
                  </Flex>
                  <Flex flexDirection="column" gap={0.5} flex={1}>
                    <Text
                      sx={{
                        ...CHART_TYPOGRAPHY.body5,
                        color: CHART_COLORS.textDark,
                      }}
                    >
                      {event.description}
                    </Text>
                    <Flex gap={1} alignItems="center">
                      <Text
                        sx={{
                          ...CHART_TYPOGRAPHY.body5,
                          color: CHART_COLORS.textLabel,
                        }}
                      >
                        {event.actionDate && `${formatTime(event.actionDate)}`}
                      </Text>
                      <Text
                        sx={{
                          ...CHART_TYPOGRAPHY.body5,
                          color: CHART_COLORS.textLabel,
                        }}
                      >
                        |
                      </Text>
                      {isAppointmentRequestOperation(
                        event?.requiringActionOperation ||
                          event?.patientRequiringActionOperation
                      ) ? (
                        <Flex gap={1.5} alignItems="center">
                          <LinkButton
                            onClick={() =>
                              handleApproveAppointmentRequest(event)
                            }
                          >
                            Approve
                          </LinkButton>
                          <Text
                            sx={{
                              ...CHART_TYPOGRAPHY.body5,
                              color: CHART_COLORS.textLabel,
                            }}
                          >
                            |
                          </Text>
                          <LinkButton
                            onClick={() => openDenyAppointmentRequest(event)}
                          >
                            Deny
                          </LinkButton>
                        </Flex>
                      ) : (
                        <LinkButton onClick={() => handleAction(event)}>
                          {getActionName(
                            event?.requiringActionOperation ||
                              event?.patientRequiringActionOperation,
                            event?.taskAction
                          )}
                        </LinkButton>
                      )}
                    </Flex>
                  </Flex>
                </Flex>
              );
            })}
          </React.Fragment>
        ))}

        {isLoadingData && (
          <div
            style={{
              minHeight: "30px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            loading...
          </div>
        )}

        {!isLoadingData && isEndOfList && (
          <div
            style={{
              minHeight: "30px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontSize: "12px",
              color: CHART_COLORS.textLabel,
            }}
          >
            No more data available
          </div>
        )}
      </Flex>
    </>
  );
};

export default PatientRequiringAction;
